import type { Session, EndPoint } from "../types/session.ts";
import { getConfig, exchangeSignInToken } from "./client.ts";
import {
  isJwtOptIn,
  setEphPriv,
  consumeEphPriv,
  setTokens as setCockpitTokens,
  getTokens as getCockpitTokens,
  decodeJwtClaims,
  type CockpitTokens,
} from "../auth/cockpitJwt.ts";
import { generateEphKeyPair, unsealCockpitToken } from "../auth/cockpitUnseal.ts";
import { Revision as clientVersion } from "../revisionInfo.ts";

const RETURN_PATH_KEY = "webadmincc-return-path";

/**
 * Always async since the JWT opt-in path needs to mint an ephemeral ECDH keypair before the
 * redirect. Callers can fire-and-forget — `void redirectToSignIn()` — since the function ends
 * with `window.location.href = …` anyway.
 */
export async function redirectToSignIn(): Promise<void> {
  // Preserve current path so we can return after sign-in. React Router's
  // basename is applied automatically by navigate(), so the saved path must
  // be relative to basename — strip the BASE_URL prefix or post-callback
  // navigate would double it (/beta/beta/dashboard).
  const baseTrim = import.meta.env.BASE_URL.replace(/\/+$/, "");
  let pathname = window.location.pathname;
  if (baseTrim && pathname.startsWith(baseTrim)) pathname = pathname.slice(baseTrim.length) || "/";
  const currentPath = pathname + window.location.search + window.location.hash;
  if (currentPath && currentPath !== "/") {
    sessionStorage.setItem(RETURN_PATH_KEY, currentPath);
  }
  const { SignInServiceUrl } = getConfig();
  // BASE_URL has leading + trailing slash (e.g. "/" or "/beta/") — joined with
  // "auth/callback" (no leading slash) so /beta/auth/callback when basePath=/beta/.
  const redirect = `${window.location.origin}${import.meta.env.BASE_URL}auth/callback`;
  const theme = document.documentElement.getAttribute("data-theme") || "light";
  const vision = document.documentElement.getAttribute("data-vision") || "";
  // Brand pack ("instantsystems" default = attribute absent). Sent so SignInService
  // can later render matching logo/copy; consumer side not yet implemented.
  const brand = document.documentElement.getAttribute("data-theme-pack") || "instantsystems";

  // JWT opt-in: localStorage key `sign_in_jwt` present (any value) flips us to the
  // cockpitjwt SignInService mode. Generate a per-sign-in P-521 ECDH keypair, ship the pub
  // as eph_pub, stash the priv in sessionStorage for the callback unseal. Failure to gen
  // falls back to legacy mode rather than blocking sign-in.
  let mode = "COCKPIT";
  let ephPubB64: string | null = null;
  if (isJwtOptIn()) {
    try {
      const { publicRaw132, privJwk } = await generateEphKeyPair();
      setEphPriv(privJwk);
      mode = "cockpitjwt";
      ephPubB64 = bytesToB64url(publicRaw132);
    } catch (err) {
      console.warn("[cockpit-jwt] eph keypair gen failed, falling back to legacy", err);
    }
  }

  // POST entry: SignInService accepts redirect / mode / eph_pub / brand / public
  // as form fields and moves them into a server-side cookie before bouncing the
  // user to a clean /. Keeps sensitive-looking params out of the URL bar,
  // browser history, Referer header, and any server access log. Display-only
  // theme/vision stay on the action URL — they need to survive the bounce so
  // the inline first-paint script in App.razor / themeSwitcher.js sees them.
  const displayQs = new URLSearchParams();
  displayQs.set("theme", theme);
  if (vision) displayQs.set("vision", vision);
  const actionUrl = `${SignInServiceUrl}?${displayQs.toString()}`;
  const fields: Record<string, string> = { redirect, mode, brand };
  if (ephPubB64) fields.eph_pub = ephPubB64;
  submitHiddenForm(actionUrl, fields);
}

/** Render a hidden auto-submitting form. Document-scoped so callers don't need
 *  to manage the DOM node. */
function submitHiddenForm(action: string, fields: Record<string, string>): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  // No enctype set — default application/x-www-form-urlencoded matches what
  // SignInService's middleware reads via ctx.Request.ReadFormAsync().
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Consume the saved return path (one-time read). Returns "/" if none saved. */
export function consumeReturnPath(): string {
  const path = sessionStorage.getItem(RETURN_PATH_KEY);
  if (path) sessionStorage.removeItem(RETURN_PATH_KEY);
  return path ?? "/";
}

export async function handleSignInCallback(): Promise<Session> {
  // Token arrives in fragment (#sign_in_data=…) — never hits server logs,
  // identical path in dev (vite) and prod (nginx).
  const hashPart = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
  const signInData = new URLSearchParams(hashPart).get("sign_in_data");

  if (!signInData) {
    throw new Error("No sign_in_data found in callback");
  }

  // Scrub token from URL (history + address bar) before exchange.
  history.replaceState(null, "", window.location.pathname);

  // JWT path: if we stashed an ECDH priv before the redirect, the bundle is the cockpit-token
  // sealed envelope. Unseal locally, store tokens, synthesize a Session shell so downstream
  // cache/endpoint flow stays shape-compatible. On unseal failure we fall through to legacy —
  // covers the case where the user interrupted JWT sign-in and a stale flag survived.
  const ephPriv = consumeEphPriv();
  if (ephPriv) {
    try {
      const bundle = await unsealCockpitToken(signInData, ephPriv);
      const expUnix = Math.floor(Date.now() / 1000) + bundle.expires_in;
      setCockpitTokens({
        access: bundle.access_token,
        refresh: bundle.refresh_token,
        expUnix,
        userId: bundle.userId,
        scope: bundle.scope,
        chainId: bundle.chain_id ?? "",
      });
      const claims = decodeJwtClaims(bundle.access_token);
      const superAdmin = claims?.["superadmin"] === true;
      const superUserTools = claims?.["superusertools"] === true;
      return synthSessionForJwt(bundle.userId, superAdmin, superUserTools);
    } catch (err) {
      console.warn("[cockpit-jwt] unseal failed, attempting legacy exchange", err);
    }
  }

  return exchangeSignInToken(signInData);
}

/**
 * Build a Session-shape object from JWT claims for downstream code (authContext, RPC layer)
 * that still pivots on Session presence. Session/Ticket are zero-valued because JWT auth
 * doesn't go through Web_Admin_Auth_SP — server-side WebVerify already branches on
 * OAuth2Session and skips the DB check, so these fields are unused on the wire.
 */
function synthSessionForJwt(userId: number, superAdmin: boolean, superUserTools: boolean): Session {
  return {
    UID: userId,
    Session: "",
    Ticket: "",
    ActiveEndPointID: 0,
    SuperAdmin: superAdmin,
    SuperUserTools: superUserTools,
    Blocked: false,
    ForceChange: false,
    ModuleID: 0,
    PartnerID: 0,
    Version: 0,
    ClientVersion: String(clientVersion),
  };
}

export function persistSession(session: Session): void {
  // Strip any EndPoints the backend may still return — endpoints are fetched on demand, not cached in session.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { EndPoints: _ignored, ...clean } = session as any;
  sessionStorage.setItem("session", JSON.stringify(clean));
}

/* ── Cross-tab session bridge ───────────────────────────────────────────────
 * `sessionStorage` is per-tab, so a `target="_blank"` link lands in a new
 * tab with no session and bounces through SignIn. We stash the session in
 * `localStorage` (shared cross-tab, same origin) and reference it from a
 * short URL fragment.
 *
 * The localStorage payload is XOR-encrypted with a random per-handoff key
 * so a casual reader of localStorage only sees ciphertext. The decryption
 * key travels in the URL fragment alongside the handoff id (`#sb=<id>.<key>`).
 * JSON.parse on the wrong key fails → bad input is rejected.
 *
 * Consume is destructive (one-shot). The handoff entry is also auto-cleared
 * 10 s after creation by a scheduled timeout, so an unused or already-
 * consumed entry doesn't linger in localStorage. A short TTL fallback prunes
 * orphans whose timer was killed by a page reload.
 *
 * Cache + activeEndpoint already live in localStorage so they're visible
 * across tabs without bridging. */
const HANDOFF_PREFIX = "wac_handoff_";
const HANDOFF_LIFETIME_MS = 10 * 1000;
const HANDOFF_TTL_MS = 60 * 1000;

interface HandoffEnvelope { t: number; c: string }

function bytesToB64url(b: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]!);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/")
    + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
function xorBytes(data: Uint8Array, key: Uint8Array): Uint8Array {
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) out[i] = data[i]! ^ key[i % key.length]!;
  return out;
}

function generateHandoffId(): string {
  const a = new Uint8Array(9);
  crypto.getRandomValues(a);
  let s = "";
  for (let i = 0; i < a.length; i++) s += a[i]!.toString(36).padStart(2, "0");
  return s.slice(0, 12);
}

function pruneStaleHandoffs(): void {
  try {
    const now = Date.now();
    const remove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(HANDOFF_PREFIX)) continue;
      try {
        const env = JSON.parse(localStorage.getItem(key) || "{}") as HandoffEnvelope;
        if (!env?.t || now - env.t > HANDOFF_TTL_MS) remove.push(key);
      } catch { remove.push(key); }
    }
    for (const k of remove) localStorage.removeItem(k);
  } catch { /* ignore */ }
}

/** Bridge payload — wraps session + optional cockpit JWT tokens. New tabs spawned via
 *  `buildSessionBridgeUrl` recover JWT auth without re-running SignInService. Backward-compat
 *  consumer at `consumeSessionBridge` accepts either this shape OR a bare Session (legacy
 *  pre-JWT senders) so an in-flight handoff during deploy still succeeds. */
interface BridgePayload {
  session: Session;
  cockpitTokens?: CockpitTokens;
}

/** Build a same-origin URL that carries the current session via a short
 *  handoff token (`#sb=<id>.<key>`). Receiver decrypts on boot via
 *  `consumeSessionBridge()`. The localStorage entry self-destructs after
 *  HANDOFF_LIFETIME_MS regardless of whether it was used.
 *
 *  Callers pass SPA-relative paths (e.g. "/grid?..."); when BASE_URL is "/beta/"
 *  the path needs the basename prefix or the receiving tab opens at root and
 *  the SPA boots from / instead of /beta/. */
export function buildSessionBridgeUrl(targetPath: string, session: Session): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { EndPoints: _ignored, ...clean } = session as any;
  const cockpitTokens = getCockpitTokens() ?? undefined;
  const payload: BridgePayload = { session: clean as Session, cockpitTokens };
  pruneStaleHandoffs();
  const id = generateHandoffId();
  const key = new Uint8Array(16);
  crypto.getRandomValues(key);
  const plainBytes = new TextEncoder().encode(JSON.stringify(payload));
  const cipher = xorBytes(plainBytes, key);
  const storageKey = HANDOFF_PREFIX + id;
  // Prefix basename for absolute SPA paths. Skip if caller already supplied an
  // absolute URL (http://...) or a path that already starts with the basename.
  const baseTrim = import.meta.env.BASE_URL.replace(/\/+$/, "");
  let path = targetPath;
  if (baseTrim && path.startsWith("/") && !path.startsWith(baseTrim + "/") && path !== baseTrim) {
    path = baseTrim + path;
  }
  try {
    const env: HandoffEnvelope = { t: Date.now(), c: bytesToB64url(cipher) };
    localStorage.setItem(storageKey, JSON.stringify(env));
  } catch {
    return path;
  }
  setTimeout(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
  }, HANDOFF_LIFETIME_MS);
  const sep = path.includes("#") ? "&" : "#";
  return `${path}${sep}sb=${id}.${bytesToB64url(key)}`;
}

/** Detect a `#sb=<id>.<key>` bridge token in the URL, decrypt the matching
 *  localStorage entry, restore it into sessionStorage, remove the entry,
 *  and scrub the fragment. One-shot — the same URL won't work twice. */
export function consumeSessionBridge(): boolean {
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  const token = params.get("sb");
  if (!token) return false;
  const dot = token.indexOf(".");
  if (dot < 0) return false;
  const id = token.slice(0, dot);
  const keyB64 = token.slice(dot + 1);
  const storageKey = HANDOFF_PREFIX + id;
  let session: Session | null = null;
  let cockpitTokens: CockpitTokens | null = null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const env = JSON.parse(raw) as HandoffEnvelope;
      if (env?.t && env.c && Date.now() - env.t < HANDOFF_TTL_MS) {
        const key = b64urlToBytes(keyB64);
        const cipher = b64urlToBytes(env.c);
        const json = new TextDecoder().decode(xorBytes(cipher, key));
        const decoded = JSON.parse(json) as BridgePayload | Session;
        // New shape (post-JWT): { session, cockpitTokens? }
        // Old shape (pre-JWT in-flight): bare Session
        const wrapped = (decoded as BridgePayload).session;
        const candidate = wrapped ?? (decoded as Session);
        // JWT-issued sessions carry empty Session/Ticket strings (still truthy as field
        // presence) but the legacy gate "candidate.Session truthy" fails — accept either
        // a non-empty Session string OR a populated cockpitTokens block.
        const tokens = (decoded as BridgePayload).cockpitTokens ?? null;
        if (candidate?.UID && (candidate.Session || tokens)) {
          session = candidate;
          cockpitTokens = tokens;
        }
      }
    }
  } catch (err) {
    console.warn("session bridge parse failed", err);
  } finally {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
  }
  if (!session) return false;
  sessionStorage.setItem("session", JSON.stringify(session));
  if (cockpitTokens) {
    setCockpitTokens(cockpitTokens);
  }
  params.delete("sb");
  const remaining = params.toString();
  history.replaceState(null, "", window.location.pathname + window.location.search + (remaining ? `#${remaining}` : ""));
  return true;
}

export function loadPersistedSession(): Session | null {
  try {
    const raw = sessionStorage.getItem("session");
    if (!raw) return null;
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function clearPersistedSession(): void {
  sessionStorage.removeItem("session");
}

const CACHE_KEY_PREFIX = "initCache_";

function cacheKey(uid: number, endpointId: number): string {
  return `${CACHE_KEY_PREFIX}${uid}_${endpointId}`;
}

export function persistCache(uid: number, endpointId: number, cache: unknown): void {
  try {
    localStorage.setItem(cacheKey(uid, endpointId), JSON.stringify(cache));
  } catch {
    // localStorage full or unavailable — non-fatal
  }
}

export function loadPersistedCache(uid: number, endpointId: number): unknown | null {
  try {
    const raw = localStorage.getItem(cacheKey(uid, endpointId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Persist only the active endpoint, not the full list.
// Covers what pages need on reload (Title, ModuleID, PartnerID, CustomerID).
// Full list arrives via background WebGetUserEndpoints fetch.
const ACTIVE_ENDPOINT_KEY_PREFIX = "activeEndpoint_";

function activeEndpointKey(uid: number): string {
  return `${ACTIVE_ENDPOINT_KEY_PREFIX}${uid}`;
}

export function persistActiveEndpoint(uid: number, endpoint: EndPoint | null): void {
  try {
    if (endpoint) localStorage.setItem(activeEndpointKey(uid), JSON.stringify(endpoint));
    else localStorage.removeItem(activeEndpointKey(uid));
  } catch { /* non-fatal */ }
}

export function loadPersistedActiveEndpoint(uid: number): EndPoint | null {
  try {
    const raw = localStorage.getItem(activeEndpointKey(uid));
    if (!raw) return null;
    return JSON.parse(raw) as EndPoint;
  } catch { return null; }
}

export function clearPersistedActiveEndpoint(uid?: number): void {
  if (uid != null) {
    localStorage.removeItem(activeEndpointKey(uid));
  } else {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(ACTIVE_ENDPOINT_KEY_PREFIX));
    for (const k of keys) localStorage.removeItem(k);
  }
}

export function clearPersistedCache(uid?: number, endpointId?: number): void {
  if (uid != null && endpointId != null) {
    localStorage.removeItem(cacheKey(uid, endpointId));
  } else {
    // Clear all cached entries for this prefix
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
    for (const k of keys) localStorage.removeItem(k);
  }
}

/**
 * Shallow structural check of a persisted / freshly-merged cache. Catches cases
 * where localStorage returns JSON of the wrong shape (schema drift, half-written
 * blob, old build's payload). Downstream code assumes these arrays exist.
 */
export function isCacheShapeValid(cache: unknown): boolean {
  if (!cache || typeof cache !== "object") return false;
  const c = cache as Record<string, unknown>;
  if (!c.PageInfo || typeof c.PageInfo !== "object") return false;
  const pi = c.PageInfo as Record<string, unknown>;
  if (!Array.isArray(pi.Data)) return false;
  if (!Array.isArray(c.Permissions)) return false;
  if (!Array.isArray(c.ColumnInfo)) return false;
  return true;
}

/** Bounded auto-clear loop protection. Tracks timestamps of recent auto-clears
 *  in sessionStorage (per-tab, ephemeral). Caller compares against MAX_AUTO_CACHE_CLEARS. */
const CLEAR_ATTEMPTS_KEY = "initCache_clear_attempts";
const CLEAR_ATTEMPT_WINDOW_MS = 60_000;
export const MAX_AUTO_CACHE_CLEARS = 2;

function readAttempts(): number[] {
  try {
    const raw = sessionStorage.getItem(CLEAR_ATTEMPTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((t: unknown): t is number => typeof t === "number") : [];
  } catch { return []; }
}

/** Record an auto-clear attempt. Returns the count of attempts within the window (including this one). */
export function recordCacheClearAttempt(): number {
  const now = Date.now();
  const arr = readAttempts().filter(t => now - t < CLEAR_ATTEMPT_WINDOW_MS);
  arr.push(now);
  try { sessionStorage.setItem(CLEAR_ATTEMPTS_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
  return arr.length;
}

export function resetCacheClearAttempts(): void {
  try { sessionStorage.removeItem(CLEAR_ATTEMPTS_KEY); } catch { /* ignore */ }
}
